import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { logger } from '@fiverr-private/obs';

const LAST_RESEND_TIME_KEY = 'fiverr_activation.last_resend_time';

export const RESEND_COOLDOWN = 30_000; // 30 seconds

interface ResendCooldownContextProps {
    isUnavailable: boolean;
    startCooldown: () => boolean;
    commitCooldown: () => void;
    cancelCooldown: () => void;
}

export const ResendCooldownContext = React.createContext<ResendCooldownContextProps | undefined>(undefined);

const logStorageWarn = (action: string) => {
    logger.warn({
        message: `Resend Activation Email Warning: Failed to ${action} last resend time in local storage`,
        package: '@fiverr-private/code_activation',
    });
};

const removeStoredLastResendTime = () => {
    try {
        localStorage.removeItem(LAST_RESEND_TIME_KEY);
    } catch {
        logStorageWarn('remove');
    }
};

const readStoredLastResendTime = (): number => {
    let stored: string | null;

    try {
        stored = localStorage.getItem(LAST_RESEND_TIME_KEY);
    } catch {
        logStorageWarn('read');
        return 0;
    }

    if (!stored) {
        return 0;
    }

    const parsed = Number(stored);
    if (!Number.isFinite(parsed)) {
        removeStoredLastResendTime();
        return 0;
    }

    return parsed;
};

const writeStoredLastResendTime = (lastResendTime: number) => {
    try {
        localStorage.setItem(LAST_RESEND_TIME_KEY, lastResendTime.toString());
    } catch {
        logStorageWarn('write');
    }
};

const ResendCooldownRoot = ({ children }: { children: React.ReactNode }) => {
    const [lastResendTime, setLastResendTime] = useState(0);

    const lastResendTimeRef = useRef(lastResendTime);
    lastResendTimeRef.current = lastResendTime;

    const expiryTimer = useRef<NodeJS.Timeout>();

    useEffect(() => {
        setLastResendTime(readStoredLastResendTime());
    }, []);

    useEffect(() => {
        clearTimeout(expiryTimer.current);
        expiryTimer.current = undefined;

        if (!lastResendTime) {
            return undefined;
        }

        const expire = () => {
            expiryTimer.current = undefined;
            removeStoredLastResendTime();
            setLastResendTime(0);
        };

        const remaining = lastResendTime + RESEND_COOLDOWN - Date.now();
        if (remaining <= 0) {
            expire();
            return undefined;
        }

        expiryTimer.current = setTimeout(expire, remaining);

        return () => {
            clearTimeout(expiryTimer.current);
            expiryTimer.current = undefined;
        };
    }, [lastResendTime]);

    const startCooldown = useCallback(() => {
        const now = Date.now();

        if (lastResendTimeRef.current + RESEND_COOLDOWN > now) {
            return false;
        }

        lastResendTimeRef.current = now;
        setLastResendTime(now);

        return true;
    }, []);

    const commitCooldown = useCallback(() => {
        writeStoredLastResendTime(lastResendTimeRef.current);
    }, []);

    const cancelCooldown = useCallback(() => {
        lastResendTimeRef.current = 0;
        setLastResendTime(0);
    }, []);

    const value = useMemo(
        () => ({
            isUnavailable: lastResendTime + RESEND_COOLDOWN > Date.now(),
            startCooldown,
            commitCooldown,
            cancelCooldown,
        }),
        [lastResendTime, startCooldown, commitCooldown, cancelCooldown]
    );

    return <ResendCooldownContext.Provider value={value}>{children}</ResendCooldownContext.Provider>;
};

/**
 * Shares the resend cooldown between every component that can trigger a resend, so a resend from
 * one disables the others instead of each keeping its own flag, timer and `localStorage` read.
 *
 * Nests safely: when an ancestor already provides the cooldown this is a passthrough, which lets
 * every package entry point wrap itself without splitting the state.
 */
export const ResendCooldownProvider = ({ children }: { children: React.ReactNode }) => {
    const parent = useContext(ResendCooldownContext);

    if (parent) {
        return <>{children}</>;
    }

    return <ResendCooldownRoot>{children}</ResendCooldownRoot>;
};

export const useResendCooldownContext = () => {
    const value = useContext(ResendCooldownContext);

    if (value === undefined) {
        throw new Error('useResendCooldownContext must be used within a ResendCooldownProvider');
    }

    return value;
};
